import { type UseModalState } from "@src/lib/ui-react";
import { FacilityRole, fetchFacilityRoles } from "src/api/facilityRoles";
import { HCF_USER_EVENTS } from "src/constants/firebaseEvents";
import {
  fetchUsers,
  removeUser,
  unLinkUser,
  updateUserInfo,
} from "src/containers/facilityUser/api";
import {
  FacilityNotification,
  fillDefaultAlertSettings,
} from "src/containers/facilityUser/helpers";
import { createNotifyRoles } from "src/containers/myAccount/Notification";
import { getSortedRoles } from "src/containers/myAccount/utilities";
import {
  generateAddEditLogData,
  generateLogData,
  generateNotificationLogData,
} from "src/containers/teamMembers/logs";
import { SessionType } from "src/modules/interface";
import { initialState } from "src/modules/myAccount";
import { MYACCOUNT_ACTIONS } from "src/modules/myAccount/actions";
import {
  IFormData,
  IFormValue,
  IUser,
  TypeAlertSettings,
  TypeGranularControl,
  type IAlertSetting,
} from "src/modules/myAccount/types";
import { logout } from "src/modules/session";
import store from "src/store";
import { getErrorMessage } from "src/utils/errors";
import isEqual from "lodash/isEqual";
import omit from "lodash/omit";
import omitBy from "lodash/omitBy";
import { Dispatch } from "redux";
import { ITeamMember } from "../../containers/teamMembers/useTeamMembersAPIHook";
import { logEvent } from "@src/appV2/lib/analytics";
import { showErrorToast } from "@src/appV2/lib/Notifications";
import { getFacilityUser } from "@src/api/facilityUsers";
import { LDFlagSet } from "launchdarkly-react-client-sdk";

const waitForAnalyticEventsToComplete = (delay = 500) =>
  new Promise((res) => setTimeout(res, delay));

export const toggleLoading = (payload: boolean) => ({
  type: MYACCOUNT_ACTIONS.LOADING,
  payload,
});

export const setUser = (userData: IUser) => ({
  type: MYACCOUNT_ACTIONS.SET_USER,
  payload: userData,
});

export const fetchCurrentUser = async (dispatch: Dispatch) => {
  const { user: currentUser } = await getFacilityUser();

  dispatch(setUser(currentUser as IUser));
};

export const fetchUser = async (dispatch: Dispatch, userId: string, email: string) => {
  const [currentUser] = await fetchUsers({
    facilityId: userId,
    isArchived: false,
    filter: {
      email,
    },
  });

  dispatch(setUser(currentUser as IUser));
};

export const handleFormToggle = (payload: boolean) => ({
  type: MYACCOUNT_ACTIONS.TOGGLE_FORM,
  payload,
});

export const setFormData = (dispatch: Dispatch, data?: IFormValue) => {
  const { myAccount, session } = store.getState() as SessionType;
  const { user: sessionUser, profile } = session;
  const { user, flags } = myAccount;

  if (!(sessionUser && session)) {
    return;
  }

  const access = user?.roles || sessionUser.access;

  let userInfo: Partial<IFormValue> = {};
  if (data && user) {
    userInfo = omitBy(data, (value, key) => isEqual(user[key], value));
  } else {
    if (data) {
      userInfo = data;
    }
  }

  const alertSettings: Partial<TypeAlertSettings> = user?.alertSettings || { EMAIL: {}, SMS: {} };
  const granularControl = fillDefaultAlertSettings(
    alertSettings,
    access,
    false,
    flags,
    user?.permissions
  ) as TypeGranularControl;

  let formData: IFormData = {
    facilityId: profile.userId,
    firebaseId: user?.firebaseId || "",
    performedBy: session.user?._id ?? "",
    userId: (user?._id || session.user?._id) ?? "",
    userInfo,
  };

  const userInfoNotifyData = createNotifyRoles(
    {
      ...formData.userInfo,
      notify: user?.notify,
    },
    granularControl
  );
  formData = {
    ...formData,
    userInfo: { ...userInfoNotifyData },
  };

  dispatch({
    type: MYACCOUNT_ACTIONS.SET_FORM_DATA,
    payload: formData,
  });
};

export const submitForm = async (dispatch: Dispatch, data?: IFormValue, shouldLogout = false) => {
  const {
    session: { user: sessionUser, profile, admin, type },
  } = store.getState() as SessionType;
  if (data) {
    dispatch(toggleLoading(true));
  }
  setFormData(dispatch, data);

  const { myAccount } = store.getState() as SessionType;
  const { formData } = myAccount;

  const updateProperties = omit(formData?.userInfo ?? {}, "alertSettings", "notify");
  const eventData = generateAddEditLogData(
    formData?.userInfo as ITeamMember,
    sessionUser,
    admin,
    type,
    profile.userId,
    myAccount.user
  );

  try {
    if (!sessionUser) {
      return;
    }
    await updateUserInfo(formData);
    if (Object.keys(updateProperties).length) {
      logEvent(HCF_USER_EVENTS.EDIT_MY_ACCOUNT_INFORMATION, {
        ...eventData,
        action: "edit",
        success: true,
      });
    }
    void fetchUser(dispatch, profile.userId, sessionUser?.email);

    if (shouldLogout) {
      store.dispatch(logout());
      window.location.reload();
    }
  } catch (err) {
    showErrorToast(getErrorMessage(err));
    if (Object.keys(updateProperties).length) {
      logEvent(HCF_USER_EVENTS.EDIT_MY_ACCOUNT_INFORMATION, {
        ...eventData,
        action: "edit",
        success: false,
      });
    }
  } finally {
    if (!shouldLogout) {
      dispatch(toggleLoading(false));
      dispatch(handleFormToggle(false));
    }
  }
};

export const unlinkUser = async (unlinkModalState: UseModalState) => {
  const { myAccount, session } = store.getState() as SessionType;
  const { admin, adminUserId, userId } = session;
  const { user } = myAccount;
  const {
    type,
    profile: { userId: workplaceId },
  } = session;

  const event = generateLogData(userId, admin, adminUserId, type, workplaceId, user?._id || "");
  try {
    await unLinkUser({
      userId: user?._id,
      facilityId: session.profile.userId,
    });
    logEvent(HCF_USER_EVENTS.UNLINK_TEAM_MEMBER, {
      ...event,
      success: true,
    });
    unlinkModalState.closeModal();
    await waitForAnalyticEventsToComplete();

    store.dispatch(logout());
    window.location.reload();
  } catch (error) {
    showErrorToast(getErrorMessage(error));
    logEvent(HCF_USER_EVENTS.UNLINK_TEAM_MEMBER, {
      ...event,
      success: false,
    });
  }
};

export const deactivateUser = async (deactivateModalState: UseModalState) => {
  const {
    myAccount: { user },
    session,
  } = store.getState() as SessionType;
  const {
    admin,
    adminUserId,
    userId,
    type,
    profile: { userId: workplaceId },
  } = session;

  const event = generateLogData(userId, admin, adminUserId, type, workplaceId, user?._id || "");
  try {
    await removeUser({
      userId: user?._id,
      firebaseId: user?.firebaseId,
      performedBy: admin ? adminUserId : user,
      facilityId: userId,
    });
    logEvent(HCF_USER_EVENTS.DEACTIVATE_TEAM_MEMBER, {
      ...event,
      success: true,
    });
    deactivateModalState.closeModal();

    await waitForAnalyticEventsToComplete();
    store.dispatch(logout());
    window.location.reload();
  } catch (error) {
    showErrorToast(getErrorMessage(error));
    logEvent(HCF_USER_EVENTS.DEACTIVATE_TEAM_MEMBER, {
      ...event,
      success: true,
    });
  }
};

export const toggleModal = (payload: boolean) => ({
  type: MYACCOUNT_ACTIONS.TOGGLE_MODAL,
  payload,
});

export const setModalData = (
  type: string,
  alert: IAlertSetting,
  property: string,
  originalValue: string
) => ({
  type: MYACCOUNT_ACTIONS.SET_MODAL_DATA,
  payload: {
    type,
    alert,
    property,
    originalValue,
  },
});

export const pushNotification = async (pushEnabled: boolean) => {
  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { braze } = window as any;
  if (!braze.isPushBlocked() || !pushEnabled) {
    if (pushEnabled) {
      if (!braze.isPushPermissionGranted() && !braze.isPushBlocked()) {
        braze.requestPushPermission();
      }
      braze
        .getUser()
        .setPushNotificationSubscriptionType(braze.User.NotificationSubscriptionTypes.OPTED_IN);
    } else if (!pushEnabled) {
      braze
        .getUser()
        .setPushNotificationSubscriptionType(braze.User.NotificationSubscriptionTypes.UNSUBSCRIBED);
    }
  }
  return braze.isPushBlocked() as boolean;
};

export const updateNotification = async (
  dispatch: Dispatch,
  value: string,
  alertType: string,
  property: string,
  member?: IUser
) => {
  const { myAccount, session } = store.getState() as SessionType;
  const { user: sessionUser, profile, admin, type } = session;
  const { user: loggedInUser, showModal, flags } = myAccount;
  const user = member || loggedInUser;

  if (!user) {
    return;
  }
  let alertSettingsClone = { ...user.alertSettings };
  const notifyClone = { ...user.notify };
  const existingData = { ...alertSettingsClone[alertType]?.[property] };

  // Generate alertSettings for preference that aren't part of the user payload.
  if (!(alertSettingsClone[alertType]?.[property] || alertType === "WEB_PUSH")) {
    const defaultAlertSettings = fillDefaultAlertSettings(
      user.alertSettings,
      user.roles,
      false,
      flags
    );
    const alertSettingToUpdate = defaultAlertSettings[alertType].find(
      ({ action }) => action === property
    );
    alertSettingsClone = {
      ...user.alertSettings,
      [alertType]: {
        ...user.alertSettings?.[alertType],
        [property]: alertSettingToUpdate.default,
      },
    };
  }

  if (alertType === "WEB_PUSH") {
    const pushValue = value === "on";
    notifyClone[alertType] = { All: pushValue };
    const isPushBlocked = await pushNotification(pushValue);
    if (isPushBlocked) {
      return;
    }
  } else {
    const propertyValue = alertSettingsClone[alertType][property];

    propertyValue.enabled = value !== "off";
    propertyValue.batch = value === "daily";

    if (value === "daily") {
      dispatch(toggleModal(!showModal));
      dispatch(
        setModalData(
          alertType,
          propertyValue,
          property,
          // eslint-disable-next-line no-nested-ternary
          existingData.batch ? "Daily" : existingData.enabled ? "On" : "Off"
        )
      );
    }
  }

  // eslint-disable-next-line no-nested-ternary
  let existingValue = existingData.batch ? "Daily" : existingData.enabled ? "On" : "Off";
  if (property === "web_push") {
    existingValue = user.notify?.WEB_PUSH?.All ? "On" : "Off";
  }
  const eventData = generateNotificationLogData(
    property,
    existingValue,
    value,
    sessionUser,
    admin,
    type,
    profile.userId,
    user
  );

  dispatch(
    setUser({
      ...user,
      alertSettings: alertSettingsClone,
      notify: notifyClone,
    })
  );
  configureAlertSettings(dispatch);

  if (value !== "daily") {
    await submitForm(dispatch);
    logEvent(HCF_USER_EVENTS.EDIT_MY_NOTIFICATION_PREFERENCES, {
      ...eventData,
    });
    if (sessionUser) {
      void fetchUser(dispatch, profile.userId, sessionUser.email);
    }
  }
};

export const updateBatchNotification = async (
  dispatch: Dispatch,
  batchTime: { hour: number; meridiem: string }
) => {
  const { myAccount, session } = store.getState() as SessionType;
  const { hour, meridiem } = batchTime;
  const { user, modalData } = myAccount;
  const { user: sessionUser, profile, admin, type } = session;
  if (!(user && modalData && sessionUser)) {
    return;
  }
  const alertSettingsClone = { ...user.alertSettings };

  alertSettingsClone[modalData.type][modalData.property].time =
    meridiem === "pm" ? hour + 12 : hour;

  dispatch(setUser({ ...user, alertSettings: alertSettingsClone }));
  await submitForm(dispatch);
  const eventData = generateNotificationLogData(
    modalData.property,
    modalData.originalValue,
    "daily",
    sessionUser,
    admin,
    type,
    profile.userId,
    user
  );
  logEvent(HCF_USER_EVENTS.EDIT_MY_NOTIFICATION_PREFERENCES, {
    ...eventData,
  });
  dispatch(toggleModal(false));

  void fetchUser(dispatch, profile.userId, sessionUser.email);
};

export const setRoles = (payload: FacilityRole[]) => ({
  type: MYACCOUNT_ACTIONS.SET_ROLES,
  payload,
});

export const fetchAllRoles = async (dispatch: Dispatch) => {
  const roles = await fetchFacilityRoles();
  dispatch(setRoles(roles));
  const defaultUserRole = getSortedRoles(roles);
  dispatch(setUserRoles(defaultUserRole.map(({ _id }) => _id)));
};

export const setUserRoles = (payload: string[]) => ({
  type: MYACCOUNT_ACTIONS.SET_USER_ROLES,
  payload,
});

export const setRedirectUrl = (payload: string) => ({
  type: MYACCOUNT_ACTIONS.SET_REDIRECT_URL,
  payload,
});

export const setFlags = (flags: LDFlagSet) => ({
  type: MYACCOUNT_ACTIONS.SET_FLAGS,
  payload: flags,
});

export const configureAlertSettings = (dispatch: Dispatch, customUser?: IUser) => {
  const { user: loggedInUser, flags } = store.getState().myAccount;
  const user = customUser || loggedInUser;
  if (!(user?.alertSettings && flags)) {
    return;
  }

  const defaultAlertSettings = fillDefaultAlertSettings(
    user?.alertSettings,
    user?.roles,
    false,
    flags,
    user.permissions
  );

  const alertSettings = Object.entries(defaultAlertSettings).map<
    [string, Record<string, FacilityNotification>]
  >(([key, value]) => {
    const alertSetting = value.map<[string, FacilityNotification]>((item) => [item.action, item]);
    return [key, Object.fromEntries(alertSetting)];
  });

  dispatch(setAlertSettings(alertSettings));
};

export const setAlertSettings = (
  payload: Array<[string, Record<string, FacilityNotification>]>
) => ({
  type: MYACCOUNT_ACTIONS.SET_ALERT_SETTINGS,
  payload: Object.fromEntries(payload),
});

export const resetState = () => ({
  type: MYACCOUNT_ACTIONS.RESET_STATE,
  payload: initialState,
});
